import { Button, Input, Skeleton } from "@atlas/core";
import { useTranslation } from "@atlas/sdk";
import { ChevronLeft, ChevronRight } from "lucide-react";

interface IHomeSolutionsPaginationProps {
  totalCount: number;
  pageIndex: number;
  pageCount: number;
  rangeFrom: number;
  rangeTo: number;
  onPageChange: (pageIndex: number) => void;
  isLoading?: boolean;
  rangeTranslationKey?: string;
  rangeDefaultValue?: string;
}

export function HomeSolutionsPagination({
  totalCount,
  pageIndex,
  pageCount,
  rangeFrom,
  rangeTo,
  onPageChange,
  isLoading = false,
  rangeTranslationKey = "hub.home.solutions_range",
  rangeDefaultValue = "{{from}} - {{to}} of {{total}} solutions",
}: IHomeSolutionsPaginationProps) {
  const { t } = useTranslation();

  if (isLoading) {
    return <Skeleton className="h-8 w-56 shrink-0" />;
  }

  const safePageCount = Math.max(1, pageCount);
  const safePageIndex = Math.min(Math.max(0, pageIndex), safePageCount - 1);
  const currentPageDisplay = safePageIndex + 1;
  const disableNav = totalCount === 0;
  const canPrev = !disableNav && safePageIndex > 0;
  const canNext = !disableNav && safePageIndex < safePageCount - 1;

  const goToPage = (next: number) => {
    onPageChange(Math.min(Math.max(0, next), safePageCount - 1));
  };

  const commitPageInput = (raw: string) => {
    const parsed = Number.parseInt(raw, 10);
    if (!Number.isFinite(parsed)) return;
    goToPage(parsed - 1);
  };

  return (
    <div className="flex shrink-0 items-center gap-3">
      <span className="whitespace-nowrap text-sm font-medium text-muted-foreground">
        {t(rangeTranslationKey, {
          defaultValue: rangeDefaultValue,
          from: rangeFrom,
          to: rangeTo,
          total: totalCount,
        })}
      </span>

      <div className="flex items-center gap-1">
        <Button
          type="button"
          variant="ghost"
          size="icon"
          disabled={!canPrev}
          aria-label={t("hub.home.pagination_previous", {
            defaultValue: "Go to previous page",
          })}
          onClick={() => goToPage(safePageIndex - 1)}>
          <ChevronLeft />
        </Button>

        <div className="flex items-center gap-1.5">
          <Input
            key={safePageIndex}
            variant="sm"
            fieldWidth="min"
            className="tabular-nums"
            defaultValue={String(currentPageDisplay)}
            disabled={disableNav}
            aria-label={t("hub.home.pagination_current_page", {
              defaultValue: "Current page",
            })}
            onBlur={(event) => commitPageInput(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                (event.target as HTMLInputElement).blur();
              }
            }}
          />
          <span className="text-sm text-muted-foreground">
            {t("hub.home.pagination_of_pages", {
              defaultValue: "of {{total}}",
              total: safePageCount,
            })}
          </span>
        </div>

        <Button
          type="button"
          variant="ghost"
          size="icon"
          disabled={!canNext}
          aria-label={t("hub.home.pagination_next", {
            defaultValue: "Go to next page",
          })}
          onClick={() => goToPage(safePageIndex + 1)}>
          <ChevronRight />
        </Button>
      </div>
    </div>
  );
}
